import { pick } from "lodash";
import flatten from "lodash/flatten";
import { createSelector } from "reselect";
import { createAssign } from "robodux";
import { call, delay, put, select } from "saga-query";

import { ensureArray } from "@app/service/helpers";
import { api, thunks } from "@app/state/apis/api";
import * as appConst from "@app/state/constants";
import {
  addNewGroup,
  canGenerateTemporaryToken,
  createNewGroup,
  getTemporaryToken,
  removeNewGroup,
  selectAllGroupsList,
  selectedGroupData,
  updateGroup,
} from "@app/state/units/groups";
import { selectMaxRole } from "@app/state/units/login";
import { appNotify } from "@app/state/units/notify";

import { selectCurrentUiDrawer } from "./maindrawer";

import type { RootState } from "@app/state/rootState";
import type {
  TGroupTreeListItem,
  HostedOnboardingSummary,
  IGroupMember,
  ISUEnvironment0,
} from "@app/types";
const REPO_GROUPS_TREELIST = "ui/groups/treelist";
type TRepoGroupTreeList = {
  currentItem: TGroupTreeListItem | undefined;
};

export const repoGroupTreeList = createAssign<TRepoGroupTreeList>({
  name: REPO_GROUPS_TREELIST,
  initialState: {
    currentItem: undefined,
  },
});

const selectGroupTreeStore = (state: RootState) => state[REPO_GROUPS_TREELIST];

const addEnvironment = (
  parentId: number,
  parentText: string
): TGroupTreeListItem => {
  return {
    id: `${parentId}-enviroment`,
    parentId: parentId,
    ptlTitle: "Ecosistem",
    ptlClassName: "ptl-enviroment",
    ptlGroup: "group",
    ptlCfgPage: appConst.GROUPS_PAGES.ENVIRONMENT,
    ptlSortPosition: 2,
    ptlParentText: parentText,
  } as TGroupTreeListItem;
};
const addDirInfo = (
  parentId: number,
  parentText: string
): TGroupTreeListItem => {
  return {
    id: `${parentId}-dirinfo`,
    parentId: parentId,
    ptlTitle: "Directoare",
    ptlClassName: "ptl-dirinfo",
    ptlGroup: "group",
    ptlCfgPage: appConst.GROUPS_PAGES.DIRINFO,
    ptlSortPosition: 3,
    ptlParentText: parentText,
  } as TGroupTreeListItem;
};
const addBuilder = (
  parentId: number,
  parentText: string
): TGroupTreeListItem => {
  return {
    id: `${parentId}-builder`,
    parentId: parentId,
    ptlTitle: "Builder",
    ptlClassName: "ptl-builder",
    ptlGroup: "group",
    ptlCfgPage: appConst.GROUPS_PAGES.BUILDER,
    ptlSortPosition: 4,
    ptlParentText: parentText,
  } as TGroupTreeListItem;
};

export const selectCurrentGroupsTreeList = createSelector(
  selectGroupTreeStore,
  (state) => state?.currentItem
);

export const selectCurrentGroupsTreeListId = createSelector(
  selectCurrentGroupsTreeList,
  (state) => state?.id
);

const EvnironmentKeys = [
  "GROUPID",
  "PERSEED",
  "DBAUID",
  "DBSRV_ABR",
  "KIT_SRV",
  "LICENSE_SHORT_NAME",
];
export const selectEvironmentStored = createSelector(
  selectAllGroupsList,
  selectCurrentGroupsTreeList,
  (allGroups, current): ISUEnvironment0 => {
    const group = allGroups[current?.parentId || current.id] as IGroupMember;
    return pick(group, EvnironmentKeys) as ISUEnvironment0;
  }
);
export function buildGroupsTreeList(
  groups: Record<string | number, IGroupMember>,
  onboardings: HostedOnboardingSummary[]
) {
    const provisioningPerseeds = new Set(
      onboardings
        .filter(
          (onboarding) =>
            onboarding.status !== "SERVER_READY" &&
            onboarding.status !== "ABANDONED"
        )
        .map((onboarding) => onboarding.perseed.toUpperCase())
    );
    const visibleGroups = ensureArray(Object.values(groups)).filter(
      (group: IGroupMember) =>
        group.GROUPID === -1 ||
        !provisioningPerseeds.has(group.PERSEED.toUpperCase())
    );

    const groupItems = visibleGroups.map((group: IGroupMember) => {
      return {
        id: +group.GROUPID,
        parentId: null, // important! to be null
        ptlTitle: group.GROUP_NAME,
        ptlDetail: group.PERSEED,
        ptlClassName: "group",
        ptlGroup: "group",
        ptlCfgPage: appConst.GROUPS_PAGES.GROUP,
        ptlSortPosition: 1,
        ptlMeta: JSON.stringify(group),
        ptlParentText: "",
        ptlIsActive: group.IS_ACTIVE === 1,
        ptlLifecycle: group.GROUPID === -1 ? "DRAFT" : "FINALIZED",
      } as TGroupTreeListItem;
    });

    const finalizedPerseeds = new Set(
      visibleGroups
        .filter((group: IGroupMember) => group.GROUPID > 0)
        .map((group: IGroupMember) => group.PERSEED.toUpperCase())
    );
    const onboardingItems = onboardings
      .filter((onboarding) => onboarding.status !== "ABANDONED")
      .filter(
        (onboarding) =>
          onboarding.status !== "SERVER_READY" ||
          !finalizedPerseeds.has(onboarding.perseed.toUpperCase())
      )
    .map((onboarding) => {
      const reviewAttempt = onboarding.attempts
        ?.filter((attempt) =>
          ["CONFLICT", "RETRYABLE_FAILURE"].includes(attempt.result)
        )
        .at(-1);
      return {
        id: `onboarding:${onboarding.id}`,
        parentId: null,
        ptlTitle:
              onboarding.request?.organization.legalName ||
              onboarding.legalName ||
              onboarding.perseed,
            ptlDetail: onboarding.perseed,
            ptlClassName: "group onboarding",
            ptlGroup: "group",
            ptlCfgPage: appConst.GROUPS_PAGES.GROUP,
            ptlSortPosition: 1,
            ptlMeta: JSON.stringify(onboarding),
            ptlParentText: "",
            ptlIsActive: true,
        ptlLifecycle: "PROVISIONING",
        ptlOnboardingId: onboarding.id,
        ptlStatus: onboarding.status,
        ptlReviewSummary: reviewAttempt
          ? [
              reviewAttempt.errorCode,
              reviewAttempt.errorMessage,
              JSON.stringify(reviewAttempt.evidence),
            ]
              .filter(Boolean)
              .join(" | ")
          : undefined,
      } as TGroupTreeListItem;
    });

    const usualChilds = groupItems.map((group) => {
      if (["xxxx-xxxx", undefined].includes(group.ptlDetail)) return [];
      if (+group.id === -1) return [];
      const environmentNode = addEnvironment(+group.id, group.ptlTitle);
      const dirInfoNode = addDirInfo(+group.id, group.ptlTitle);
      const builderNode = addBuilder(+group.id, group.ptlTitle);
      return [environmentNode, dirInfoNode, builderNode];
    });
    return [...onboardingItems, ...groupItems, ...flatten(usualChilds)];
}

export const selectGroupsTreeList = createSelector(
  selectAllGroupsList,
  (groups) => buildGroupsTreeList(groups, [])
);

// if new group is added to the list then notity the components
// to show the apropiate controls
export const selectHasNewGroupDefined = createSelector(
  selectAllGroupsList,
  (groups) => {
    const group2 = ensureArray(Object.values(groups));
    if (!group2?.length) return false;
    const found = group2.some((group: IGroupMember) => {
      return group.GROUPID === -1;
    });
    return found;
  }
);

/* -------------------------------------------------------------------------- */
/*                               setGroupTreeList                             */

export const setGroupTreeList = thunks.create<TGroupTreeListItem>(
  "/setCurrentGroupsTreeList",
  function* (ctx, next) {
    const crtstate = yield* select(selectGroupTreeStore);
    try {
      yield* put(
        repoGroupTreeList.actions.set({ ...crtstate, currentItem: ctx.payload })
      );
      yield* next();
    } finally {
      void 0;
    }
  }
);

export const newGroup = thunks.create("tree/addGroup", function* (ctx, next) {
  yield* call(addNewGroup.run, addNewGroup());
  yield* delay(100);
  const groups = yield* select(selectGroupsTreeList);
  const newGroup = (groups as TGroupTreeListItem[]).filter(
    (group: TGroupTreeListItem) => +group.id === -1
  );
  yield* put(repoGroupTreeList.actions.set({ currentItem: newGroup[0] }));
  yield* next();
});

//cancel edit or add
export const cancelGroup = thunks.create(
  "tree/cancelGroup",
  function* (ctx, next) {
    const crtstate = yield* select(selectGroupTreeStore);
    if (crtstate.currentItem?.id === -1) {
      yield* call(removeNewGroup.run, removeNewGroup());
      yield* put(repoGroupTreeList.actions.set({ currentItem: undefined }));
    }
    yield* next();
  }
);

export const adoptHostedOnboarding = thunks.create<TGroupTreeListItem>(
  "tree/adoptHostedOnboarding",
  function* (ctx, next) {
    yield* call(removeNewGroup.run, removeNewGroup());
    yield* put(repoGroupTreeList.actions.set({ currentItem: ctx.payload }));
    yield* next();
  }
);

// CREATE GROUP
export const createGroup = thunks.create<IGroupMember>(
  "tree/createGroup",
  function* (ctx, next) {
    const newGroup = yield* call(
      createNewGroup.run,
      createNewGroup(ctx.payload)
    );
    const { ok, data } = newGroup.json;
    if (ok) {
      const newGroupID = data?.[0]?.GROUPID || -1;
      const groups = yield* select(selectGroupsTreeList);
      const newGroup = (groups as TGroupTreeListItem[]).filter(
        (group: TGroupTreeListItem) => group.id === newGroupID
      );
      yield* put(repoGroupTreeList.actions.set({ currentItem: newGroup[0] }));
      yield* call(
        appNotify.run,
        appNotify({
          message: "Group created",
          type: "success",
          position: "bottom",
        })
      );
    }
    // console.log("newGroup", newGroup);
    // show virtual group in the tree ???
    // put the new group in the list//
    yield* next();
  }
);

// UPDATE GROUP
export const updateSuGroup = thunks.create<IGroupMember>(
  "tree/updateSuGroup",
  function* (ctx, next) {
    const updata = yield* call(updateGroup.run, updateGroup(ctx.payload));
    const { ok, data } = updata.json || {};
    if (ok) {
      yield* call(
        appNotify.run,
        appNotify({
          message: "Group updated",
          type: "success",
          position: "bottom",
        })
      );
    }
    yield* next();
  }
);

export const generateTempToken = thunks.create(
  "tree/generateTempToken",
  function* (ctx, next) {
    const maxRole = yield* select(selectMaxRole);
    if (maxRole !== "SWS") return;
    const theSelectedTreeId = yield* select(selectCurrentGroupsTreeListId);
    const theSelectedOrgData = yield* select((s) =>
      selectedGroupData(s, theSelectedTreeId)
    );
    const selectedTreeItem = yield* select(selectCurrentGroupsTreeList);
    if (!canGenerateTemporaryToken(selectedTreeItem, theSelectedOrgData)) {
      return;
    }
    yield* call(
      getTemporaryToken.run,
      getTemporaryToken({ GROUPID: theSelectedOrgData?.GROUPID })
    );
    yield* next();
  }
);
type TInsiderClient = {
  AMAID: number;
  PERSEED: string;
  GROUPID: number;
  GROUP_NAME: string;
};

const emptyGroup = {
  AMAID: 0,
  PERSEED: "",
  GROUPID: 0,
  GROUP_NAME: "",
};

export const selectInsiderContext = createSelector(
  selectAllGroupsList,
  selectCurrentUiDrawer,
  (groups_, uidraw): TInsiderClient => {
    const groups = ensureArray(Object.values(groups_));
    if (!groups) {
      return emptyGroup;
    }
    const { currentPage, insider } = uidraw;
    if (!currentPage.includes(appConst.DRAWER_PAGES.INSIDERS) || !insider) {
      return emptyGroup;
    }
    const ckecks = groups.filter(
      (g) => g.INSIDERS_READY === true && g.PERSEED === insider
    );

    if (!ckecks.length || ckecks.length > 1) {
      return emptyGroup;
    }

    const theGroup = groups.find((g) => g.PERSEED === insider);
    if (!theGroup) {
      return emptyGroup;
    }
    const { ME_AMAXAVO, PERSEED, GROUPID, GROUP_NAME } = theGroup;
    return {
      AMAID: ME_AMAXAVO,
      PERSEED,
      GROUPID,
      GROUP_NAME,
    };
  }
);
